/**
 * The content rule a TinyMCE body paints under (`styles/tinyMCEStyles.ts`,
 * `.mce-content-body { line-height: 1; … }`) resolves the body's line-height
 * to a length; a body the rule has not reached yet resolves it to `normal`,
 * and every line in it is then as tall as its face's `normal` — 1.17× on a
 * display face, enough to push a one-line name over its ceiling and have it
 * fitted where every other surface keeps it (the dev9 corpus, fh-05). The
 * settle before a load result checks for that state and gives the rule a
 * few frames to land, the way it waits for faces.
 */
export const TEXT_BOX_STYLES_MAX_FRAMES = 30

export type TextBoxStylesOutcome =
  | { status: 'ready'; frames: 0 }
  | { status: 'ready-after'; frames: number; unready: 'body' | 'run' }
  | { status: 'unstyled'; frames: number; unready: 'body' | 'run' }

// A browser resolves every element's line-height, so the one state to catch
// is `normal`; an empty reading is a test document without a cascade.
const isLength = (lineHeight: string): boolean => lineHeight !== 'normal'

/** Every element holding text under the body: the runs whose line boxes the body's height is made of. */
const textRunsUnder = (body: HTMLElement): HTMLElement[] => {
  const runs = new Set<HTMLElement>()
  const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()
  while (node) {
    const parent = node.parentElement
    if (parent && (node as Text).data.trim() !== '') runs.add(parent)
    node = walker.nextNode()
  }
  return Array.from(runs)
}

/**
 * Which element still resolves `line-height: normal`: the body, or one
 * under it (a block whose strut sets the line box, or a run), or none. The
 * body inherits a number to everything under it, so an element at `normal`
 * while the body is a length is a rule that reached it late (the dev9
 * corpus, fh-05 on a fast load: the body already a length, the 80pt line
 * still at its face's `normal`, 125px for a line that paints at 107).
 */
export const textBoxStylesUnready = (
  body: HTMLElement,
): 'body' | 'run' | null => {
  if (!isLength(getComputedStyle(body).lineHeight)) return 'body'
  for (const element of Array.from(body.querySelectorAll<HTMLElement>('*'))) {
    if (!isLength(getComputedStyle(element).lineHeight)) return 'run'
  }
  return null
}

/** True once the body's and every run's computed line-height is a length, not `normal`. */
export const textBoxStylesReady = (body: HTMLElement): boolean =>
  textBoxStylesUnready(body) === null

export type LineHeightReading = {
  /** The element's computed line-height in px, or null when it resolved `normal`. */
  lineHeightPx: number | null
  fontSizePx: number
}

export type TallestRunLineHeight = {
  run: LineHeightReading
  /** The run's block — whose strut sets the line box too — or null when the run sits in the body itself. */
  block: LineHeightReading | null
}

const BLOCK_SELECTOR =
  'p, h1, h2, h3, h4, h5, h6, li, td, th, blockquote, pre, div'

const lineHeightReadingOf = (
  element: HTMLElement,
): LineHeightReading | null => {
  const cs = getComputedStyle(element)
  const fontSizePx = Number.parseFloat(cs.fontSize)
  if (!Number.isFinite(fontSizePx)) return null
  const lineHeightPx = Number.parseFloat(cs.lineHeight)
  return {
    fontSizePx,
    lineHeightPx: Number.isFinite(lineHeightPx) ? lineHeightPx : null,
  }
}

/**
 * The computed line-height and font-size of the tallest run under the body
 * and of its block — the two boxes a line's height is the larger of: the
 * run's inline box and the block's strut — read at the same instant as the
 * body's height, so a load result taken under a late style shows its ratio
 * (1.17 for a display face at `normal`, 1 for the content rule) on either,
 * whether or not the settle saw the state. Null with no text.
 */
export const tallestRunLineHeight = (
  body: HTMLElement,
): TallestRunLineHeight | null => {
  let tallestElement: HTMLElement | null = null
  let tallest: LineHeightReading | null = null
  for (const run of textRunsUnder(body)) {
    const reading = lineHeightReadingOf(run)
    if (!reading) continue
    if (tallest !== null && reading.fontSizePx <= tallest.fontSizePx) continue
    tallest = reading
    tallestElement = run
  }
  if (tallest === null || tallestElement === null) return null
  const blockElement = tallestElement.closest<HTMLElement>(BLOCK_SELECTOR)
  const block =
    blockElement && blockElement !== body && body.contains(blockElement)
      ? lineHeightReadingOf(blockElement)
      : null
  return { run: tallest, block }
}

/**
 * The tallest line's pitch in px, for the load rule's floor: the block's
 * line box when the block sets one, else the run's, else the run's size
 * under the browser's `normal` line-height, which no face makes tighter
 * than its own size. Null when nothing was read.
 */
export const tallestLinePitchPx = (
  tallest: TallestRunLineHeight | null,
): number | null => {
  if (tallest === null) return null
  const block = tallest.block?.lineHeightPx ?? null
  const run = tallest.run.lineHeightPx ?? tallest.run.fontSizePx
  const pitch = Math.max(block ?? 0, run)
  return pitch > 0 ? pitch : null
}

const readingStamp = (reading: LineHeightReading): string =>
  `${reading.lineHeightPx === null ? 'normal' : reading.lineHeightPx.toFixed(2)}/${reading.fontSizePx.toFixed(2)}`

/**
 * `run=<lineHeightPx>/<fontSizePx>;block=<lineHeightPx>/<fontSizePx>`, two
 * decimals, `normal` in place of a line-height that resolved `normal`, and
 * `block=none` for a run in the body itself.
 */
export const tallestRunLineHeightStamp = (
  reading: TallestRunLineHeight | null,
): string | null =>
  reading === null
    ? null
    : `run=${readingStamp(reading.run)};block=${reading.block ? readingStamp(reading.block) : 'none'}`

const nextFrame = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => resolve())
  })

/**
 * Null when the body's content styles have landed, so the caller can go on
 * at once; otherwise a promise that resolves once they have, or after
 * `TEXT_BOX_STYLES_MAX_FRAMES` frames without them — a body outside the
 * app's stylesheets is never waited for forever. The outcome says which.
 */
export const settleTextBoxStyles = (
  body: HTMLElement,
): Promise<TextBoxStylesOutcome> | null => {
  const unready = textBoxStylesUnready(body)
  if (unready === null) return null
  if (typeof requestAnimationFrame !== 'function') return null
  return (async () => {
    let lastUnready = unready
    for (let frames = 1; frames <= TEXT_BOX_STYLES_MAX_FRAMES; frames += 1) {
      await nextFrame()
      const stillUnready = textBoxStylesUnready(body)
      if (stillUnready === null)
        return { status: 'ready-after', frames, unready: lastUnready }
      lastUnready = stillUnready
    }
    return {
      status: 'unstyled',
      frames: TEXT_BOX_STYLES_MAX_FRAMES,
      unready: lastUnready,
    }
  })()
}

/** `ready`, `ready-after:<frames>:<body|run>` or `unstyled:<frames>:<body|run>`. */
export const textBoxStylesOutcomeStamp = (
  outcome: TextBoxStylesOutcome | null,
): string =>
  outcome === null || outcome.status === 'ready'
    ? 'ready'
    : `${outcome.status}:${outcome.frames}:${outcome.unready}`
